// Application version metadata
// Update this file when releasing a new version

export const APP_VERSION = '1.7.1';
export const APP_VERSION_LABEL = 'v1.7.1';
export const APP_VERSION_DATE = 'Juin 2026';
export const APP_VERSION_NAME = 'Enhancements';

// Format a Date as "Mercredi 06 mai 2026" (FR)
export const formatFrenchDate = (date = new Date()) => {
  try {
    const days = ['dimanche', 'lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi'];
    const months = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
    const d = new Date(date);
    const dayName = days[d.getDay()];
    const dayNum = String(d.getDate()).padStart(2, '0');
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    return `${dayName.charAt(0).toUpperCase() + dayName.slice(1)} ${dayNum} ${month} ${year}`;
  } catch {
    return '';
  }
};

// Changelog entries (most recent first)
// release_date format: ISO YYYY-MM-DD
export const VERSION_HISTORY = [
  {
    version: '1.7.1',
    label: 'v1.7.1',
    name: 'Améliorations (restore fiable, récap partageable)',
    release_date: '2026-06-01',
    highlights: [
      'Restauration fiabilisée : le code de confirmation reste valide même si le backend redémarre (persistance Mongo + disque)',
      'Rechargement automatique de l\'application après une restauration réussie',
      'Onglet Versions : historique des dernières restaurations (date, auteur, nb de documents)',
      'Vue d\'exécution : dialogue « Reprendre / Recommencer » élégant (fini la popup native du navigateur)',
      'Fin de séance : bouton « Partager le récap » (image PNG à envoyer à son coach)',
    ],
  },
  {
    version: '1.7.0',
    label: 'v1.7.0',
    name: 'Ring de progression & Restauration',
    release_date: '2026-06-01',
    highlights: [
      'Vue d\'exécution : "ring de progression rounds" sous le timer (1 pastille par round, s\'allume au fur et à mesure)',
      'Pastille du round en cours pulsée + halo cyan ; pastilles restantes en contour ; responsive au-delà de 8/12 rounds',
      'Paramètres → Versions : "Restaurer un backup" avec vérification en 2 étapes (aperçu + code de confirmation à recopier + case d\'avertissement)',
      'Instantané de sécurité automatique de la base avant chaque restauration + journal d\'audit',
    ],
  },
  {
    version: '1.6.1',
    label: 'v1.6.1',
    name: 'Rounds UX (Vue d\'exécution)',
    release_date: '2026-05-18',
    highlights: [
      'Vue d\'exécution des Tables d\'Apnée : compteur "Round X / Y — Phase x/2" (au lieu de "Étape X / Y")',
      'Aperçu suivant : phase restante du round en cours + paire complète du prochain round',
      'Note de sauvegarde partielle : "round X/Y, phase x/2" pour une lecture plus naturelle',
      'Récap completion : "Rounds complétés : X / Y"',
    ],
  },
  {
    version: '1.6.0',
    label: 'v1.6.0',
    name: 'Security Hardening (Sprint 1)',
    release_date: '2026-05-18',
    highlights: [
      'CORS verrouillé par liste d\'origines (env-driven, fin du wildcard)',
      'Anti-brute-force sur /auth/login (5 tentatives → lockout 15min)',
      'Rate limiter global slowapi (200 req/min par IP)',
      'Endpoint /api/health avec ping MongoDB',
      'Endpoints /api/admin/backup et /backup/stats (admin only)',
    ],
  },
  {
    version: '1.5.1',
    label: 'v1.5.1',
    name: 'Polish & Versioning',
    release_date: '2026-05-06',
    highlights: [
      'Correction "PV" : Poumons Vides (Empty Lung) au lieu de Plein Volume',
      'Date du jour en français sur le footer (ex: Mercredi 06 mai 2026)',
      'Nouvel onglet "Historique des versions" dans Paramètres globaux',
    ],
  },
  {
    version: '1.5.0',
    label: 'v1.5.0',
    name: 'Settings & Mobile Polish',
    release_date: '2026-05-06',
    highlights: [
      'Onglets Settings : Sons vocaux (TTS configurable) + Logos d\'agence (upload PNG/SVG)',
      'Tables d\'Apnée : import Excel/CSV + import PDF avec IA (détection auto type+difficulté)',
      'Vue d\'exécution mobile-responsive (React Portal, z-10001, cercle adaptatif)',
      'Top bar d\'exécution opaque avec toutes les infos (nom, type, difficulté, étape)',
      'Login modal max-h-90vh + scroll interne pour mobile',
      'Sous-titre dashboard : "activités d\'apnée sportive"',
      'Préfixe PDF d\'entraînement : "Seance_" au lieu de "Entrainement_"',
    ],
  },
  {
    version: '1.4.1',
    label: 'v1.4.1',
    name: 'Bulk Registrations Bugfix',
    release_date: '2026-05-05',
    highlights: [
      'Dialog z-index au-dessus du menu app (z-9999/10000/10001)',
      'Dialog max-height + scroll interne',
      'Fix items glissés invisibles (flex-1 + min-h-0)',
    ],
  },
  {
    version: '1.4.0',
    label: 'v1.4.0',
    name: 'Bulk Registrations Edition',
    release_date: '2026-05-04',
    highlights: [
      'Inscriptions multiples (drag & drop apnéistes)',
      'Pré-remplissage des inscriptions existantes',
      'Édition inline AP + date d\'annonce',
      'Recherche par nom/prénom dans la liste source',
    ],
  },
  {
    version: '1.3.2',
    label: 'v1.3.2',
    name: 'Apnea UX Polish',
    release_date: '2026-05-04',
    highlights: [
      'Vue liste pour Tables d\'Apnée + filtre par difficulté',
      'Affichage durée mm:ss sous Ventilation/Apnée durant l\'exécution',
      'Bouton "Terminer la table" (sauvegarde partielle)',
      'Voix anticipée -1s (compensation latence TTS)',
      'Fix bug Export PDF (jspdf 3.x ESM API)',
    ],
  },
  {
    version: '1.3.1',
    label: 'v1.3.1',
    name: 'Apnea Sessions',
    release_date: '2026-05-04',
    highlights: [
      'Historique d\'exécution des Tables d\'Apnée',
      'Lien PB → carrière (Performance STA)',
      'Fix 401 pré-login',
      'Refactor TrainingSessionsTab',
    ],
  },
  {
    version: '1.3.0',
    label: 'v1.3.0',
    name: 'Apnea Tables Edition',
    release_date: '2026-05-04',
    highlights: [
      'Module Tables d\'Apnée (training statique)',
      'Footer de version sur le dashboard',
      'README bilingue FR/EN',
    ],
  },
  {
    version: '1.2.0',
    label: 'v1.2.0',
    name: 'Multi-Zone & Auth',
    release_date: '2026-05-03',
    highlights: [
      'Configuration de zones drag & drop',
      'Workflow Pause/Reprendre/Terminer événement',
      'Migration progressive bcrypt depuis SHA-256',
      'Duplicate Competition feature',
    ],
  },
  {
    version: '1.1.0',
    label: 'v1.1.0',
    name: 'Spectator & Refactor',
    release_date: '2026-05-02',
    highlights: [
      'Vue Spectateur temps réel (WebSocket + polling)',
      'Refactor backend en routeurs modulaires',
      'Moteur de pénalités auto (AIDA + CMAS)',
    ],
  },
  {
    version: '1.0.0',
    label: 'v1.0.0',
    name: 'Initial Release',
    release_date: '2026-05-01',
    highlights: [
      'Compétitions CRUD',
      'Événement live avec scoring',
      'Registre apnéistes',
    ],
  },
];
